import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, CircularProgress, Select, MenuItem,
  FormControl, InputLabel, Divider, List, ListItem, ListItemText, ListItemAvatar, Avatar,
  Grid, TextField, InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

import { LEADS_ADMIN_API, API_BASE_URL } from '../apiConfig';
const PHOTO_BASE_URL = "https://storage.googleapis.com/poc-colorin/fotos-de-espacios/";

const LeadsManager = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [users, setUsers] = useState([]);
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const LIMIT = 50;

  const fetchLeads = useCallback(async (isLoadMore = false) => {
    if (isLoadMore) setLoadingMore(true);
    else setLoading(true);

    try {
      const nextPage = isLoadMore ? page + 1 : 1;
      let url = `${LEADS_ADMIN_API}?page=${nextPage}&limit=${LIMIT}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterAssignedTo) url += `&assigned_to=${filterAssignedTo}`;
      
      const response = await axios.get(url);
      const data = response.data;

      if (isLoadMore) {
        setLeads(prev => [...prev, ...data]);
        setPage(nextPage);
      } else {
        setLeads(data);
        setPage(1);
      }

      setHasMore(data.length === LIMIT);
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [page, filterStatus, filterAssignedTo]);

  useEffect(() => {
    fetchLeads();
    if (isAdmin && users.length === 0) {
      axios.get(`${API_BASE_URL.replace('/espacios', '/admin/users')}`)
        .then(res => setUsers(res.data))
        .catch(err => console.error("Error fetching users:", err));
    }
  }, [isAdmin, fetchLeads]);

  const handleUpdateStatus = async (leadId, newStatus) => {
    setUpdating(true);
    try {
      await axios.patch(`${LEADS_ADMIN_API}/${leadId}`, { status: newStatus });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (error) {
      console.error("Error updating lead status:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAssignLead = async (leadId, userId) => {
    setUpdating(true);
    try {
      await axios.patch(`${LEADS_ADMIN_API}/${leadId}`, { assigned_to: userId });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, assigned_to: userId } : l));
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({ ...selectedLead, assigned_to: userId });
      }
    } catch (error) {
      console.error("Error assigning lead:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm(`¿Eliminar permanentemente el lead #${leadId}?`)) return;
    setDeleting(leadId);
    try {
      await axios.delete(`${LEADS_ADMIN_API}/${leadId}`);
      setLeads(prev => prev.filter(l => l.id !== leadId));
      if (selectedLead && selectedLead.id === leadId) setSelectedLead(null);
    } catch (error) {
      console.error("Error deleting lead:", error);
      alert('Error al eliminar el lead.');
    } finally {
      setDeleting(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'primary';
      case 'Contacted': return 'success';
      case 'Discarded': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('es-DO', { 
      day: '2-digit', month: '2-digit', year: 'numeric', 
      hour: '2-digit', minute: '2-digit' 
    });
  };

  const filteredLeads = leads.filter(l => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (l.client_name && l.client_name.toLowerCase().includes(term)) ||
      (l.email && l.email.toLowerCase().includes(term)) ||
      (l.company && l.company.toLowerCase().includes(term)) ||
      String(l.id).includes(term)
    );
  });

  return (
    <Box sx={{ p: 4, height: '100%', overflowY: 'auto', bgcolor: 'background.default' }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={800} color="primary" sx={{ letterSpacing: '-0.03em' }}>
              Gestión de Leads
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Seguimiento de prospectos y asignación de oportunidades comerciales.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open('/catalog', '_blank')}
            >
              Ir al Portal
            </Button>
            <Button variant="outlined" size="small" onClick={() => fetchLeads()} disabled={loading}>Actualizar</Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Buscar por texto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
            sx={{ bgcolor: 'white', borderRadius: 1, minWidth: 200 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'white' }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={filterStatus}
              label="Estado"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="New">Nuevo</MenuItem>
              <MenuItem value="Contacted">Contactado</MenuItem>
              <MenuItem value="Discarded">Descartado</MenuItem>
            </Select>
          </FormControl>

          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'white' }}>
              <InputLabel>Asignado a</InputLabel>
              <Select
                value={filterAssignedTo}
                label="Asignado a"
                onChange={(e) => setFilterAssignedTo(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {users.map(u => (
                  <MenuItem key={u.uid} value={u.uid}>{u.email}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {(filterStatus || filterAssignedTo || searchTerm) && (
            <Button 
              size="small" 
              onClick={() => {
                setFilterStatus('');
                setFilterAssignedTo('');
                setSearchTerm('');
              }}
            >
              Limpiar
            </Button>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      ) : leads.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography color="text.secondary">No hay leads registrados aún.</Typography>
        </Paper>
      ) : (
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: 4, 
          overflow: 'hidden', 
          border: '1px solid', 
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: '0 10px 30px rgba(0,0,0,0.04)'
        }}
      >
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {[
                  'ID', 'Fecha', 'Cliente', 'Contacto', 'Interés', 'Estado'
                ].concat(isAdmin ? ['Asignado A'] : []).concat(['Acciones']).map((head) => (
                  <TableCell 
                    key={head}
                    sx={{ 
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      fontWeight: 700,
                      py: 2,
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLeads.map((lead) => (
                <TableRow key={lead.id} hover>
                  <TableCell>#{lead.id}</TableCell>
                  <TableCell>{formatDate(lead.created_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{lead.client_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{lead.company || 'Personal'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption">{lead.email}</Typography>
                      </Box>
                      {lead.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption">{lead.phone}</Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`${lead.selected_billboards?.length || 0} vallas`} 
                      size="small" 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={lead.status} 
                      color={getStatusColor(lead.status)} 
                      size="small" 
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon fontSize="small" color="action" />
                        {users.find(u => u.uid === lead.assigned_to)?.name || 'Sin asignar'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Tooltip title="Ver Detalles">
                      <IconButton color="primary" onClick={() => setSelectedLead(lead)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {isAdmin && (
                      <Tooltip title="Eliminar Lead">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteLead(lead.id)}
                          disabled={deleting === lead.id}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {hasMore && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
            <Button 
              variant="text" 
              onClick={() => fetchLeads(true)} 
              disabled={loadingMore}
              sx={{ fontWeight: 700 }}
              startIcon={loadingMore ? <CircularProgress size={16} /> : null}
            >
              {loadingMore ? 'Cargando más...' : 'Cargar más leads'}
            </Button>
          </Box>
        )}
      </Paper>
      )}

      {/* Lead details Modal */}
      <Dialog open={!!selectedLead} onClose={() => setSelectedLead(null)} maxWidth="sm" fullWidth>
        {selectedLead && (
          <>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Detalle del Lead #{selectedLead.id}
              <Chip label={selectedLead.status} color={getStatusColor(selectedLead.status)} size="small" />
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>INFORMACIÓN DEL CLIENTE</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">{selectedLead.client_name}</Typography>
                    {selectedLead.company && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon fontSize="small" color="action" />
                        <Typography variant="body2">{selectedLead.company}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">{selectedLead.email}</Typography>
                      </Box>
                      {selectedLead.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{selectedLead.phone}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" gutterBottom>MENSAJE</Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1 }}>
                    {selectedLead.message || "Sin mensaje adicional."}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" gutterBottom>VALLAS DE INTERÉS</Typography>
                  <List dense>
                    {selectedLead.selected_billboards?.map((code) => (
                      <ListItem key={code}>
                        <ListItemAvatar>
                          <Avatar 
                            variant="rounded" 
                            src={`${PHOTO_BASE_URL}${code}.jpg`}
                            sx={{ width: 50, height: 35 }}
                          />
                        </ListItemAvatar>
                        <ListItemText 
                          primary={code} 
                          secondary="Revisar en inventario" 
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                
                {isAdmin && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="primary" gutterBottom>ASIGNACIÓN DE LEAD</Typography>
                    <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                      <InputLabel>Asignar a</InputLabel>
                      <Select
                        label="Asignar a"
                        value={selectedLead.assigned_to || ''}
                        onChange={(e) => handleAssignLead(selectedLead.id, e.target.value)}
                        disabled={updating}
                      >
                        <MenuItem value=""><em>Sin asignar</em></MenuItem>
                        {users.map(u => (
                          <MenuItem key={u.uid} value={u.uid}>{u.name} ({u.role})</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" gutterBottom>CAMBIAR ESTADO</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button 
                      size="small" variant="outlined" color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleUpdateStatus(selectedLead.id, 'Contacted')}
                      disabled={updating || selectedLead.status === 'Contacted'}
                    >
                      Marcas como Contactado
                    </Button>
                    <Button 
                      size="small" variant="outlined" color="error"
                      startIcon={<DoNotDisturbIcon />}
                      onClick={() => handleUpdateStatus(selectedLead.id, 'Discarded')}
                      disabled={updating || selectedLead.status === 'Discarded'}
                    >
                      Descartar
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedLead(null)}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default LeadsManager;
